import React, { ComponentType } from 'react';
import { StyleSheet, View, Text } from 'react-native';
import {
  ControlProps,
  isControl,
  JsonFormsRendererRegistryEntry,
  RankedTester,
  rankWith,
} from '@jsonforms/core';
import { DispatchCell, withJsonFormsControlProps } from '@jsonforms/react';
import Colors from '../../../../constants/Colors';
import { RENDERER_RANK_DEFAULT_FALLBACK } from '../constants';

const styles = StyleSheet.create({
  inputElement: {
    flex: 1,
    paddingRight: 16,
    marginBottom: 16,
  },
  smallRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  labelInputContainer: {
    flex: 1,
    marginLeft: 0,
  },
  label: {
    color: Colors.veryDarkGray,
  },
  descriptionText: {
    color: Colors.grayInfoText,
  },
  errorText: {
    color: Colors.brightRed,
  },
});

/**
 * Tester to determine if a control can be used with this renderer.
 */
const tester: RankedTester = rankWith(
  RENDERER_RANK_DEFAULT_FALLBACK,
  isControl // built-in matcher for any controls (but not labels or groups or layouts)
);

/**
 * Base renderer to show a text input control.
 * @param props Control props to be hydrated using `withJsonFormsControlProps`.
 */
const InputControlRenderer: ComponentType<ControlProps> = (props: ControlProps) => {
  return (
    <View style={styles.inputElement} testID={`InputControl-${props.id}`}>
      <View style={styles.smallRow}>
        <View style={styles.labelInputContainer}>
          <Text style={styles.label} testID={`InputControl-${props.id}-label`}>
            {props.label}
          </Text>
          <DispatchCell
            // pass along properties used to determine and render cell
            uischema={props.uischema}
            schema={props.schema}
            renderers={props.renderers}
            cells={props.cells}
            // follow example for cell ID from vanilla renderers
            id={props.id + '-input'}
            path={props.path}
            enabled={props.enabled}
          />
          {props.errors.length > 0 ? (
            <Text style={styles.errorText} testID={`InputControl-${props.id}-error`}>
              {props.errors}
            </Text>
          ) : (
            <Text style={styles.descriptionText} testID={`InputControl-${props.id}-description`}>
              {props.description}
            </Text>
          )}
        </View>
      </View>
    </View>
  );
};

/**
 * Renderer registry entry for text input control.
 * Bundles the tester and the wrapped control for use with form.
 */
export const InputControl: JsonFormsRendererRegistryEntry = {
  tester,
  renderer: withJsonFormsControlProps(InputControlRenderer),
};
